"use client"

import { Fragment, useState, useMemo, useEffect, useTransition } from "react"
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react"
import { Menu, X, Home, ShoppingBag, User, Globe, Languages, Check, Sun, Moon } from "lucide-react"
import { useTranslations, useLocale } from "next-intl"
import { useParams, usePathname } from "next/navigation"
import { HttpTypes } from "@medusajs/types"
import ReactCountryFlag from "react-country-flag"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import FleetProfile from "@modules/layout/components/fleet-profile"
import { SearchBar } from "@modules/search/components/search-bar"
import { Button } from "@modules/common/components/button"
import { updateRegion } from "@lib/data/cart"
import { localeNames, Locale, locales } from "@/types/i18n"
import { setUserLocale } from "../language-selector/actions"
import { useTheme } from "next-themes"

interface MobileNavProps {
  regions: HttpTypes.StoreRegion[] | null
  defaultRegion?: HttpTypes.StoreRegion | null
}

// Short language codes for display
const shortLangCodes: Record<Locale, string> = {
  en: "EN",
  ru: "RU",
  cs: "CZ",
  uk: "UA",
}

const MobileNav = ({ regions, defaultRegion }: MobileNavProps) => {
  const t = useTranslations("common")
  const locale = useLocale() as Locale
  const { theme, setTheme } = useTheme()
  const [isPending, startTransition] = useTransition()
  const [isOpen, setIsOpen] = useState(false)
  const [showCountries, setShowCountries] = useState(false)
  const [showLanguages, setShowLanguages] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Avoid hydration mismatch for theme
  useEffect(() => {
    setMounted(true)
  }, [])

  const { countryCode } = useParams()
  const currentPath = usePathname().split(`/${countryCode}`)[1]

  // Build country options from regions
  const countryOptions = useMemo(() => {
    return regions
      ?.flatMap((r) =>
        r.countries?.map((c) => ({
          country: c.iso_2,
          region: r.id,
          label: c.display_name,
        })) ?? []
      )
      .sort((a, b) => (a?.label ?? "").localeCompare(b?.label ?? "")) ?? []
  }, [regions])

  // Current country
  const currentCountry = useMemo(() => {
    return countryOptions.find((o) => o?.country === countryCode)
  }, [countryOptions, countryCode])

  const navLinks = [
    { name: t("nav.home"), href: "/", icon: Home },
    { name: t("nav.store"), href: "/store", icon: ShoppingBag },
    // Shop-local Medusa account area (orders + addresses). Auth-gated routes
    // bounce through fleet SSO if not yet signed in.
    { name: t("nav.ordersAddresses"), href: "/account", icon: User },
  ]

  const close = () => setIsOpen(false)

  const handleCountryChange = (country: string) => {
    updateRegion(country, currentPath)
    setShowCountries(false)
  }

  const handleLanguageChange = (newLocale: Locale) => {
    if (newLocale === locale) {
      setShowLanguages(false)
      return
    }
    startTransition(async () => {
      await setUserLocale(newLocale)
      window.location.reload()
    })
  }

  return (
    <>
      {/* Menu Button. На мобильнике это единственный нав-индикатор,
          поэтому когда юзер залогинен — показываем его инициалы
          вместо стандартного гамбургера; иначе непонятно auth ли он.
          Клик в любом состоянии открывает меню. */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="small:hidden relative"
        aria-label={t("nav.menu")}
      >
        <Menu className="w-6 h-6" />
      </Button>

      {/* Mobile Menu Panel */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={close} className="relative z-50 small:hidden">
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xs" />
          </TransitionChild>

          {/* Panel */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="fixed inset-y-0 left-0 w-full max-w-xs bg-background border-r border-border shadow-xl">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <LocalizedClientLink
                    href="/"
                    className="text-xl font-bold text-primary"
                    onClick={close}
                  >
                    {t("footer.storeName")}
                  </LocalizedClientLink>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={close}
                    aria-label={t("buttons.close")}
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                {/* Search */}
                {defaultRegion && (
                  <div className="p-4 border-b border-border">
                    <SearchBar region={defaultRegion} onSearch={close} />
                  </div>
                )}

                {/* Navigation Links */}
                <nav className="flex-1 overflow-y-auto p-4">
                  <ul className="space-y-1">
                    {navLinks.map((item) => {
                      const Icon = item.icon
                      return (
                        <li key={item.href}>
                          <LocalizedClientLink
                            href={item.href}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-surface-subtle transition-colors"
                            onClick={close}
                          >
                            <Icon className="w-5 h-5 text-foreground-secondary" />
                            <span className="text-base font-medium">{item.name}</span>
                          </LocalizedClientLink>
                        </li>
                      )
                    })}
                  </ul>
                </nav>

                {/* Bottom Section */}
                <div className="border-t border-border p-4 space-y-2">
                  {/* Fleet identity — the ONE (fleet) login/account control */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-foreground">
                      {t("nav.account")}
                    </span>
                    <FleetProfile />
                  </div>

                  {/* Country Selector */}
                  {countryOptions.length > 0 && (
                    <div>
                      <Button
                        variant="ghost"
                        onClick={() => setShowCountries(!showCountries)}
                        className="flex items-center justify-between w-full px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <Globe className="w-5 h-5 text-foreground-secondary" />
                          <span className="text-sm text-foreground">
                            {t("country.shippingTo")}
                          </span>
                        </div>
                        {currentCountry && (
                          // @ts-ignore
                          <ReactCountryFlag
                            svg
                            style={{ width: "20px", height: "20px" }}
                            countryCode={currentCountry.country ?? ""}
                          />
                        )}
                      </Button>

                      {showCountries && (
                        <div className="mt-1 mx-2 max-h-48 overflow-y-auto rounded-lg border border-border bg-popover">
                          {countryOptions.map((option) => (
                            <Button
                              variant="ghost"
                              key={option.country}
                              onClick={() => handleCountryChange(option.country ?? "")}
                              className="h-auto p-0 rounded-none font-normal flex items-center gap-3 w-full px-3 py-2 text-sm hover:bg-surface-subtle transition-colors"
                            >
                              {/* @ts-ignore */}
                              <ReactCountryFlag
                                svg
                                style={{ width: "16px", height: "16px" }}
                                countryCode={option.country ?? ""}
                              />
                              <span className="flex-1 text-left">{option.label}</span>
                              {option.country === countryCode && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Language Selector */}
                  <div>
                    <Button
                      variant="ghost"
                      onClick={() => setShowLanguages(!showLanguages)}
                      className="flex items-center justify-between w-full px-4 py-3"
                      disabled={isPending}
                    >
                      <div className="flex items-center gap-3">
                        <Languages className="w-5 h-5 text-foreground-secondary" />
                        <span className="text-sm text-foreground">
                          {t("language.selectLanguage")}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-primary">
                        {shortLangCodes[locale]}
                      </span>
                    </Button>

                    {showLanguages && (
                      <div className="mt-1 mx-2 rounded-lg border border-border bg-popover">
                        {locales.map((code) => (
                          <Button
                            variant="ghost"
                            key={code}
                            onClick={() => handleLanguageChange(code)}
                            className="h-auto p-0 rounded-none font-normal flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-surface-subtle transition-colors"
                          >
                            <span>{localeNames[code]}</span>
                            {code === locale && (
                              <Check className="w-4 h-4 text-primary" />
                            )}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Theme Toggle */}
                  <Button
                    variant="ghost"
                    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                    className="flex items-center justify-between w-full px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      {mounted && theme === "dark" ? (
                        <Moon className="w-5 h-5 text-foreground-secondary" />
                      ) : (
                        <Sun className="w-5 h-5 text-foreground-secondary" />
                      )}
                      <span className="text-sm text-foreground">
                        {t("theme.toggle")}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-primary">
                      {mounted ? (theme === "dark" ? t("theme.dark") : t("theme.light")) : ""}
                    </span>
                  </Button>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border">
                  <p className="text-xs text-muted-foreground text-center">
                    {t("footer.copyright", { year: new Date().getFullYear() })}
                  </p>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  )
}

export default MobileNav
